import { useState } from 'react';
import { PencilSquareIcon, ArrowPathIcon, PlayIcon } from '@heroicons/react/24/outline';
import { useChartBuilderStore } from '../../store/chartBuilderStore';
import { useAuthStore } from '../../store/authStore';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';

export default function SqlEditorTab() {
  const {
    sqlQuery,
    isCustomSQL,
    chartType,
    chartConfig,
    schema,
    setSqlQuery,
    setChartConfig,
    ejectToCustomSQL,
    setPreviewData,
    setPreviewError,
  } = useChartBuilderStore();

  const { getCurrentCompanyId } = useAuthStore();
  const [localQuery, setLocalQuery] = useState(sqlQuery || '');
  const [isPreviewing, setIsPreviewing] = useState(false);

  const effectiveQuery = isCustomSQL ? localQuery : sqlQuery;

  const handleEjectToCustomSql = () => {
    setLocalQuery(sqlQuery || '');
    ejectToCustomSQL();
  };

  const handleSqlChange = (value) => {
    setLocalQuery(value);
    setSqlQuery(value);
  };

  const handleRunPreview = async () => {
    const query = isCustomSQL ? localQuery : sqlQuery;
    if (!query?.trim()) {
      toast.error('No SQL query to run');
      return;
    }

    setIsPreviewing(true);
    setPreviewError(null);

    try {
      const companyId = getCurrentCompanyId();
      const response = await apiHelpers.previewChartQuery({
        companyId,
        query,
        type: chartType || 'bar',
      });

      setPreviewData(response.data?.data || []);
      setChartConfig({
        ...chartConfig,
        query,
        config: chartConfig?.config || {},
      });
      toast.success(`Preview loaded (${response.data?.rowCount || 0} rows)`);
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Failed to preview query';
      setPreviewError(errorMsg);
      setPreviewData(null);
      toast.error(errorMsg);
    } finally {
      setIsPreviewing(false);
    }
  };

  const columns = schema?.tables?.[0]?.columns || [];

  return (
    <div className="space-y-4">
      {!isCustomSQL ? (
        <>
          <div className="flex items-center justify-between">
            <label className="label">Generated SQL (read-only)</label>
            <button
              onClick={handleEjectToCustomSql}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            >
              <PencilSquareIcon className="w-4 h-4" />
              Edit SQL
            </button>
          </div>
          <pre className="p-4 bg-surface-900 dark:bg-surface-950 text-surface-100 rounded-lg text-xs font-mono overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
            {effectiveQuery || 'No SQL generated yet. Use the Visual Builder or AI Assistant first.'}
          </pre>
          <p className="text-xs text-surface-500">
            Click &quot;Edit SQL&quot; to modify the query directly. Changes will not sync back to the
            Visual Builder.
          </p>
        </>
      ) : (
        <>
          {columns.length > 0 && (
            <div>
              <label className="label">Available Columns</label>
              <div className="flex flex-wrap gap-2 p-3 bg-surface-50 dark:bg-surface-800 rounded-lg max-h-24 overflow-y-auto">
                {columns.map((col) => (
                  <button
                    key={col.name}
                    onClick={() => handleSqlChange(localQuery + col.name)}
                    className="badge-primary cursor-pointer hover:bg-primary-200 dark:hover:bg-primary-800"
                    title="Click to insert"
                  >
                    {col.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="label">SQL Query</label>
            <textarea
              value={localQuery}
              onChange={(e) => handleSqlChange(e.target.value)}
              placeholder={`SELECT * FROM \${dataset}.table_name`}
              rows={10}
              className="input font-mono text-sm w-full"
            />
            <p className="text-xs text-surface-500 mt-1">
              Use {`\${dataset}`} as the dataset placeholder. For marts use {`\${dataset}_marts`}, for
              wide views use {`\${dataset}_wide_views`}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRunPreview}
              disabled={isPreviewing || !localQuery?.trim()}
              className="btn-primary"
            >
              {isPreviewing ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
                  Running...
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4 mr-2" />
                  Run Preview
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
